import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useFetchDeliveryRegionsQuery } from '../../redux-toolkits/user/user.slice'
import { DeliveryRegion } from '../../redux-toolkits/locations/locations.type'
import {
  DeliveryAreaRestriction,
  DeliveryAreaRestrictionMode,
} from '../../redux-toolkits/products/products.type'
import { Vendor } from '../../redux-toolkits/vendors/vendors.types'
import { ActionChip } from '../cards/actionChip'
import { EditInput } from '../inputs/editInput'

const restrictionOptions: {
  label: string
  description: string
  value: DeliveryAreaRestrictionMode
}[] = [
  {
    label: 'All selected regions',
    description: 'Available everywhere inside the selected delivery regions.',
    value: 'ALL_REGION',
  },
  {
    label: 'Only selected sub-regions',
    description: 'Available only in the sub-regions selected below.',
    value: 'ONLY_SELECTED_AREAS',
  },
]

type SelectedArea = {
  _id: string
  name: string
  parentId?: string
  parentName?: string
}

type DeliveryLocationEntry = NonNullable<Vendor['deliveryLocation']>[number]

function mapRestrictionAreas(
  restriction?: DeliveryAreaRestriction,
  regionById?: Map<string, DeliveryRegion>,
): SelectedArea[] {
  return (
    restriction?.areaIds
      ?.map((area) => {
        const region = typeof area === 'string' ? regionById?.get(area) : area
        const id = typeof area === 'string' ? area : area._id
        const parentRegion =
          region && typeof region !== 'string' ? region.parentRegion : undefined
        const parentId =
          typeof parentRegion === 'string' ? parentRegion : parentRegion?._id
        const parentName =
          typeof parentRegion === 'object' ? parentRegion.name : undefined

        return {
          _id: id,
          name: region?.name ?? id,
          parentId,
          parentName,
        }
      })
      .filter((area) => area._id) ?? []
  )
}

function getLocationId(locationId?: DeliveryLocationEntry['locationId']) {
  if (!locationId) return ''
  return typeof locationId === 'string' ? locationId : locationId._id
}

function getLocationName(locationId?: DeliveryLocationEntry['locationId']) {
  if (!locationId) return ''
  return typeof locationId === 'string' ? locationId : locationId.name
}

function getParentRegionId(parentRegion?: string | DeliveryRegion) {
  if (!parentRegion) return ''
  return typeof parentRegion === 'string' ? parentRegion : parentRegion._id
}

function getSelectedParentRegions(
  regionSource?: Vendor,
  regionById?: Map<string, DeliveryRegion>,
): SelectedArea[] {
  const seen = new Set<string>()

  return (
    regionSource?.deliveryLocation
      ?.filter((location) => location.locationType !== 'pickup')
      .map((location) => {
        const locationId = getLocationId(location.locationId)
        const locationName = getLocationName(location.locationId)
        const region = locationId ? regionById?.get(locationId) : undefined
        const parentRegion =
          typeof region?.parentRegion === 'string'
            ? regionById?.get(region.parentRegion)
            : region?.parentRegion
        const resolvedRegion = parentRegion ?? region

        return {
          _id: resolvedRegion?._id ?? locationId,
          name: resolvedRegion?.name ?? locationName,
        }
      })
      .filter((region) => {
        if (!region._id || seen.has(region._id)) {
          return false
        }

        seen.add(region._id)
        return true
      }) ?? []
  )
}

function normalizeRestrictionMode(
  mode?: DeliveryAreaRestrictionMode,
): DeliveryAreaRestrictionMode {
  return mode === 'ONLY_SELECTED_AREAS' ? mode : 'ALL_REGION'
}

export const VendorDeliveryAreaRestriction = ({
  vendor,
  deliveryRegionSource,
  loading,
  onModify,
  className,
  readOnly = false,
}: {
  vendor?: Vendor
  deliveryRegionSource?: Vendor
  loading?: boolean
  onModify: (
    data: {
      deliveryAreaRestriction: {
        mode: DeliveryAreaRestrictionMode
        areaIds: string[]
      }
    },
    handleClose: () => void,
  ) => void
  className?: string
  readOnly?: boolean
}) => {
  const [editing, setEditing] = useState(false)
  const [mode, setMode] = useState<DeliveryAreaRestrictionMode>('ALL_REGION')
  const [areas, setAreas] = useState<SelectedArea[]>([])
  const [search, setSearch] = useState('')
  const savedAreaIds =
    vendor?.deliveryAreaRestriction?.mode === 'ALL_REGION'
      ? []
      : (vendor?.deliveryAreaRestriction?.areaIds ?? []).map((area) =>
          typeof area === 'string' ? area : area._id,
        )

  const { data: areasData, isFetching } = useFetchDeliveryRegionsQuery(
    {
      page: 1,
      limit: 1000,
      isAvailable: true,
    },
    {
      skip: !editing,
      refetchOnMountOrArgChange: true,
    },
  )
  const { data: displayAreasData } = useFetchDeliveryRegionsQuery(
    {
      page: 1,
      limit: 1000,
      isAvailable: true,
    },
    {
      skip: savedAreaIds.length < 1 && !editing,
      refetchOnMountOrArgChange: true,
    },
  )
  const regionById = new Map(
    (areasData?.data.docs ?? []).map((region) => [region._id, region]),
  )
  const displayRegionById = new Map(
    (displayAreasData?.data.docs ?? areasData?.data.docs ?? []).map(
      (region) => [region._id, region],
    ),
  )
  const regionSource = deliveryRegionSource ?? vendor
  const selectedParentRegions = getSelectedParentRegions(
    regionSource,
    displayRegionById.size > 0 ? displayRegionById : regionById,
  )
  const selectedParentIds = selectedParentRegions.map((region) => region._id)
  const selectableSubRegions =
    areasData?.data.docs
      .filter((region) => {
        const parentId = getParentRegionId(region.parentRegion)
        return parentId && selectedParentIds.includes(parentId)
      })
      .map((region) => ({
        _id: region._id,
        name: region.name,
        parentId: getParentRegionId(region.parentRegion),
        parentName:
          typeof region.parentRegion === 'object'
            ? region.parentRegion.name
            : regionById.get(getParentRegionId(region.parentRegion))?.name,
      })) ?? []
  const selectedAreaIds = new Set(areas.map((area) => area._id))
  const normalizedSearch = search.trim().toLowerCase()
  const groupedSubRegions = selectedParentRegions
    .map((parent) => ({
      parent,
      areas: selectableSubRegions.filter((area) => {
        const belongsToParent = area.parentId === parent._id
        const matchesSearch =
          !normalizedSearch ||
          area.name.toLowerCase().includes(normalizedSearch) ||
          area.parentName?.toLowerCase().includes(normalizedSearch)

        return belongsToParent && matchesSearch
      }),
    }))
    .filter((group) => group.areas.length > 0)

  const toggleArea = (area: SelectedArea) => {
    if (selectedAreaIds.has(area._id)) {
      setAreas(areas.filter((selected) => selected._id !== area._id))
      return
    }

    setAreas([...areas, area])
  }

  useEffect(() => {
    setMode(normalizeRestrictionMode(vendor?.deliveryAreaRestriction?.mode))
    setAreas(
      mapRestrictionAreas(vendor?.deliveryAreaRestriction, displayRegionById),
    )
  }, [vendor?.deliveryAreaRestriction, displayAreasData, areasData])

  const requiresAreas = mode !== 'ALL_REGION'

  return (
    <div className={className}>
      <EditInput
        title="Delivery area restriction"
        editing={editing}
        loading={loading}
        disabled={requiresAreas && areas.length < 1}
        onEditClick={
          readOnly
            ? undefined
            : () => {
                setEditing(true)
              }
        }
        onCancel={() => {
          setMode(
            normalizeRestrictionMode(vendor?.deliveryAreaRestriction?.mode),
          )
          setAreas(
            mapRestrictionAreas(
              vendor?.deliveryAreaRestriction,
              displayRegionById,
            ),
          )
          setSearch('')
          setEditing(false)
        }}
        onEditSave={() => {
          onModify(
            {
              deliveryAreaRestriction: {
                mode,
                areaIds: mode === 'ALL_REGION' ? [] : areas.map((a) => a._id),
              },
            },
            () => {
              setEditing(false)
            },
          )
        }}
      >
        {({ isEditing }) => {
          if (!isEditing) {
            return (
              <div className="flex flex-wrap gap-3">
                <ActionChip
                  name={
                    restrictionOptions.find(
                      (option) =>
                        option.value ===
                        normalizeRestrictionMode(
                          vendor?.deliveryAreaRestriction?.mode,
                        ),
                    )?.label ?? 'All selected regions'
                  }
                />
                {mapRestrictionAreas(
                  vendor?.deliveryAreaRestriction,
                  displayRegionById,
                ).map((area) => (
                  <ActionChip
                    key={area._id}
                    name={
                      area.parentName
                        ? `${area.name} - ${area.parentName}`
                        : area.name
                    }
                  />
                ))}
              </div>
            )
          }

          return (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {restrictionOptions.map((option) => {
                  const active = option.value === mode

                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={classNames(
                        'flex min-h-[112px] flex-col items-start justify-center rounded-[8px] border px-4 py-3 text-left transition-all',
                        active
                          ? 'border-black bg-black text-white'
                          : 'border-divider-gray bg-white text-black hover:border-black',
                      )}
                      onClick={() => {
                        setMode(option.value)
                        if (option.value === 'ALL_REGION') {
                          setAreas([])
                        }
                      }}
                    >
                      <span className="text-sm font-medium">
                        {option.label}
                      </span>
                      <span
                        className={classNames('mt-1 text-xs leading-5', {
                          'text-light-gray': active,
                          'text-sec-black': !active,
                        })}
                      >
                        {option.description}
                      </span>
                    </button>
                  )
                })}
              </div>

              {requiresAreas && (
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-black">
                      Parent regions
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {selectedParentRegions.length > 0 ? (
                        selectedParentRegions.map((region) => (
                          <ActionChip key={region._id} name={region.name} />
                        ))
                      ) : (
                        <span className="text-sm text-sec-black">
                          Add a delivery region first, then restrict it to
                          sub-regions.
                        </span>
                      )}
                    </div>
                  </div>

                  <input
                    type="text"
                    name="deliveryAreaRestriction"
                    placeholder="Search sub-regions"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value)
                    }}
                    disabled={selectedParentRegions.length < 1}
                    className="h-12 w-full rounded-[8px] border border-divider-gray px-4 text-sm outline-none focus:border-black disabled:cursor-not-allowed disabled:bg-light-gray/20"
                  />

                  <div className="flex max-h-[360px] flex-col gap-4 overflow-y-auto rounded-[8px] border border-divider-gray p-4">
                    {isFetching ? (
                      <span className="text-sm text-sec-black">
                        Loading sub-regions.
                      </span>
                    ) : groupedSubRegions.length > 0 ? (
                      groupedSubRegions.map((group) => (
                        <div
                          key={group.parent._id}
                          className="flex flex-col gap-2"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-medium text-black">
                              {group.parent.name}
                            </p>
                            <span className="text-xs text-sec-black">
                              {
                                group.areas.filter((area) =>
                                  selectedAreaIds.has(area._id),
                                ).length
                              }
                              /{group.areas.length} selected
                            </span>
                          </div>

                          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                            {group.areas.map((area) => {
                              const checked = selectedAreaIds.has(area._id)

                              return (
                                <label
                                  key={area._id}
                                  className={classNames(
                                    'flex min-h-[44px] cursor-pointer items-center gap-3 rounded-[8px] border px-3 py-2 text-sm transition-all',
                                    checked
                                      ? 'border-black bg-black text-white'
                                      : 'border-divider-gray bg-white text-black hover:border-black',
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      toggleArea(area)
                                    }}
                                    className="h-4 w-4 accent-black"
                                  />
                                  <span>{area.name}</span>
                                </label>
                              )
                            })}
                          </div>
                        </div>
                      ))
                    ) : (
                      <span className="text-sm text-sec-black">
                        {selectedParentRegions.length < 1
                          ? 'Select delivery regions first.'
                          : 'No sub-regions found for the selected regions.'}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {areas.map((area) => (
                      <ActionChip
                        key={area._id}
                        name={
                          area.parentName
                            ? `${area.name} - ${area.parentName}`
                            : area.name
                        }
                        onDismiss={() => {
                          setAreas(areas.filter((a) => a._id !== area._id))
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        }}
      </EditInput>
    </div>
  )
}
